import { useEffect, useRef } from 'react';
import type { HeightmapAsset, PatternLayer, ReliefFinish } from '@/core/types';
import { renderHeightPreview } from '../preview';

/**
 * A rendered thumbnail of a layer stack.
 *
 * It is drawn by the same height field that builds the mesh, so what a card
 * shows is what the panel will be. Rendering happens on an idle callback to
 * keep a grid of twenty cards from blocking the first paint.
 */

interface ThumbnailProps {
  layers: readonly PatternLayer[];
  finish?: ReliefFinish;
  assets?: readonly HeightmapAsset[];
  /** Panels across; two reveals whether a motif continues over a joint. */
  panels?: number;
  size?: number;
  alt?: string;
}

const cache = new Map<string, ImageData>();

export function Thumbnail({ layers, finish, assets, panels = 2, size = 128, alt }: ThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Layers fully determine the image, so their serialised form is a sound key.
  const key = JSON.stringify([
    layers.map((layer) => [
      layer.patternId,
      layer.params,
      layer.blend,
      layer.amount,
      layer.enabled,
      layer.transform,
    ]),
    finish,
    panels,
    size,
  ]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const cached = cache.get(key);
    if (cached) {
      canvas.getContext('2d')?.putImageData(cached, 0, 0);
      return;
    }

    let cancelled = false;
    const draw = (): void => {
      if (cancelled || !canvasRef.current) return;
      renderHeightPreview(canvasRef.current, layers, { finish, assets, panels });

      const context = canvasRef.current.getContext('2d');
      if (context) {
        // Bounded so a long session cannot grow the cache without limit.
        if (cache.size > 120) cache.clear();
        cache.set(key, context.getImageData(0, 0, size, size));
      }
    };

    // Safari only shipped requestIdleCallback recently, so fall back to a task.
    const supportsIdle = typeof window.requestIdleCallback === 'function';
    const handle = supportsIdle
      ? window.requestIdleCallback(draw, { timeout: 400 })
      : window.setTimeout(draw, 0);

    return () => {
      cancelled = true;
      if (supportsIdle) window.cancelIdleCallback(handle);
      else clearTimeout(handle);
    };
  }, [key, layers, finish, assets, panels, size]);

  return (
    <canvas
      ref={canvasRef}
      className="thumbnail"
      width={size}
      height={size}
      role="img"
      aria-label={alt}
    />
  );
}
